import { borderRadius, colors } from '@/theme'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Box } from '@mui/material'
import { useLayoutEffect, useMemo, useRef } from 'react'
import { Toolbar } from './Toolbar'
import { dehydrateVariables, hydrateVariables, VariableNode } from './variableNode'

interface RichTextEditorProps {
  content: string
  onChange: (html: string) => void
  disabled?: boolean
  placeholder?: string
  height?: number | string
  showVariables?: boolean
}

// StarterKit already bundles Link and Underline — configuring them here
// instead of adding separate extension instances avoids Tiptap's "duplicate
// extension names" warning.
const STARTER_KIT_EXTENSION = StarterKit.configure({
  link: { openOnClick: false, autolink: false }
})

export function RichTextEditor({
  content,
  onChange,
  disabled = false,
  placeholder,
  height = 280,
  showVariables = false
}: RichTextEditorProps) {
  const showPlaceholderOnly = disabled && placeholder

  // useEditor(options, []) only evaluates `extensions` once, at construction —
  // so Placeholder.configure({ placeholder }) would otherwise freeze whatever
  // the placeholder prop was on first render. Route it through a ref instead
  // so the extension always reads the current text.
  const placeholderRef = useRef(placeholder ?? '')
  placeholderRef.current = placeholder ?? ''

  // Only matters once (useEditor consumes it at construction), but memoize
  // anyway so it isn't reallocated on every keystroke's re-render.
  const extensions = useMemo(
    () => [STARTER_KIT_EXTENSION, Placeholder.configure({ placeholder: () => placeholderRef.current }), VariableNode],
    []
  )

  // Tracks the HTML this editor itself last emitted via onChange, so the
  // content-sync effect below can tell "did the parent send back what we just
  // emitted" without re-serializing/dehydrating editor.getHTML() on every render.
  const lastEmittedRef = useRef(content)

  const editor = useEditor(
    {
      extensions,
      content: hydrateVariables(content),
      editable: !disabled,
      // Guard on docChanged: setEditable()/other non-content transactions (e.g.
      // the editable-state toggle below) also fire this callback, and reporting
      // those as content changes clobbers the content-sync effect below with
      // stale (pre-sync) HTML.
      onUpdate: ({ editor, transaction }) => {
        if (!transaction.docChanged) return
        const html = dehydrateVariables(editor.getHTML())
        lastEmittedRef.current = html
        onChange(html)
      }
    },
    []
  )

  useLayoutEffect(() => {
    if (!editor) return
    editor.setEditable(!disabled)
  }, [editor, disabled])

  useLayoutEffect(() => {
    if (!editor) return
    if (content === lastEmittedRef.current) return
    lastEmittedRef.current = content
    editor.commands.setContent(hydrateVariables(content), { emitUpdate: false })
  }, [editor, content])

  return (
    <Box
      sx={{
        position: 'relative',
        border: `1px solid ${colors.border.default}`,
        borderRadius: borderRadius.sm,
        // overflow: 'hidden',
        opacity: disabled ? 0.6 : 1,
        height,
        display: 'flex',
        flexDirection: 'column',
        '& .tiptap-scroll': {
          flex: 1,
          minHeight: 0,
          overflowY: 'auto'
        },
        '& .tiptap': {
          padding: '10px 12px',
          fontSize: 14,
          outline: 'none',
          wordBreak: 'break-word',
          overflowWrap: 'break-word'
        },
        '& .tiptap p.is-editor-empty:first-of-type::before': {
          content: 'attr(data-placeholder)',
          color: colors.text.muted,
          fontStyle: 'italic',
          float: 'left',
          height: 0,
          pointerEvents: 'none'
        },
        '& .tiptap .template-variable': {
          display: 'inline-block',
          bgcolor: colors.brand.primaryLight,
          color: colors.brand.primary,
          fontFamily: 'monospace',
          fontSize: '0.9em',
          borderRadius: borderRadius.xs,
          padding: '0 4px',
          border: `1px solid ${colors.brand.primary}22`
        },
        '& .tiptap .template-variable.ProseMirror-selectednode': {
          outline: `2px solid ${colors.brand.primary}`,
          outlineOffset: 1
        },
        '& .tiptap ul, & .tiptap ol': {
          paddingLeft: '1.5em'
        }
      }}
    >
      {editor && !showPlaceholderOnly && <Toolbar editor={editor} showVariables={showVariables} />}
      <Box className='tiptap-scroll' sx={{ visibility: showPlaceholderOnly ? 'hidden' : 'visible' }}>
        <EditorContent editor={editor} />
      </Box>
      {showPlaceholderOnly && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            color: colors.text.muted,
            fontStyle: 'italic',
            fontSize: 14,
            p: 2,
            pointerEvents: 'none'
          }}
        >
          {placeholder}
        </Box>
      )}
    </Box>
  )
}
